// CoLOS TimeDate fields: format-string parsing, offset maths, preview.
// Pure module — no window/document. Reference: docs/DATES.md.

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/**
 * LanguageValue enum: exactly the 19 values of XSD EnumLanguageChoice (docs/colos-schema/colos-legend.xsd),
 * sorted for display. "BRAZILLIAN" is the vendor's spelling.
 */
export const LANGUAGES = Object.freeze([
  'ARABIC', 'BRAZILLIAN_PORTUGUESE', 'DANISH', 'DUTCH', 'ENGLISH', 'FINNISH', 'FRENCH', 'GERMAN', 'GREEK',
  'HEBREW', 'ITALIAN', 'JAPANESE', 'MANDARIN', 'NORWEGIAN', 'POLISH', 'RUSSIAN', 'SPANISH', 'SWEDISH', 'TURKISH',
]);

/** PromptingBehaviour enum. PROMPT_YY_MM_DD = operator is asked for the offset at the printer. */
export const PROMPTING = Object.freeze(['DO_NOT_PROMPT', 'PROMPT_YY_MM_DD']);

export const MONTH_ABBR = Object.freeze(['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']);
/** Index 0 = Monday (ISO order). */
export const DAY_ABBR = Object.freeze(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);

/**
 * Term letters. `width` = natural length of the source-0 value (used for warnings only).
 * `abbr` = letter supports Source 2 (three-letter abbreviation).
 * `placeholder` = value is computed by the printer from data we do not have.
 */
export const TOKENS = Object.freeze({
  Y: { name: 'Year (4 digits)', width: 4 },
  M: { name: 'Month (2 digits)', width: 2, abbr: true },
  D: { name: 'Day of month (2 digits)', width: 2 },
  d: { name: 'Day of week', width: 1, abbr: true },
  J: { name: 'Julian day [29 Feb = 366]', width: 3 },
  j: { name: 'Julian day [29 Feb = 60]', width: 3 },
  H: { name: 'Hour (12-hour, 01–12)', width: 2 },
  h: { name: 'Hour (24-hour, 00–23)', width: 2 },
  m: { name: 'Minute', width: 2 },
  s: { name: 'Second', width: 2 },
  w: { name: 'Week of year (ISO-8601)', width: 2 },
  S: { name: 'Shift code', width: 1, placeholder: '[shift]' },
  R: { name: 'Relative year', width: 1, placeholder: '[rel. year]' },
  a: { name: 'AM/PM', width: 2 },
});

const LETTER_HINTS = {
  y: 'CoLOS has no %y — use %Y[0,2,2,0] for a 2-digit year',
  B: 'CoLOS has no %B — use %M[2,0,0,0] for the month name (JAN…DEC)',
  b: 'CoLOS has no %b — use %M[2,0,0,0] for the month name (JAN…DEC)',
};

// ---------------------------------------------------------------------------
// Parsing & validation
// ---------------------------------------------------------------------------

export class DateFormatError extends Error {
  constructor(message, errors = [message]) {
    super(message);
    this.name = 'DateFormatError';
    this.errors = errors;
  }
}

// Terms whose bracket could not be parsed; the formatter prints their raw text.
const MALFORMED = new WeakSet();

/**
 * Lenient parse: never throws.
 * @returns {{ tokens: Token[], errors: string[], warnings: string[] }}
 * Token = { kind:'term', letter, source, start, length, suppress, raw } | { kind:'literal', text }
 */
export function parseFormatDetailed(str) {
  const s = str == null ? '' : String(str);
  const tokens = [];
  const errors = [];
  const warnings = [];
  if (s.length === 0) errors.push('Date format is empty');

  let buf = '';
  let sawTerm = false;
  let warnedLeading = false;
  const flush = () => {
    if (!buf) return;
    if (!sawTerm && !warnedLeading) {
      warnings.push(`Text "${buf}" comes before the first date term — CoLOS allows separators only after a term`);
      warnedLeading = true;
    }
    tokens.push({ kind: 'literal', text: buf });
    buf = '';
  };

  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (c !== '%') { buf += c; i++; continue; }
    flush();
    if (i + 1 >= s.length) {
      errors.push('Format ends with a lone "%"');
      const tok = { kind: 'term', letter: '', source: 0, start: 0, length: 0, suppress: 0, raw: '%' };
      MALFORMED.add(tok);
      tokens.push(tok);
      break;
    }
    const rawStart = i;
    const letter = s[i + 1];
    i += 2;
    let source = 0, start = 0, length = 0, suppress = 0;
    let malformed = false;
    if (s[i] === '[') {
      const close = s.indexOf(']', i);
      const m = close >= 0 ? /^\[(\d+),(\d+),(\d+),(\d+)\]$/.exec(s.slice(i, close + 1)) : null;
      if (m) {
        source = Number(m[1]); start = Number(m[2]); length = Number(m[3]); suppress = Number(m[4]);
        i = close + 1;
      } else {
        malformed = true;
        errors.push(`Malformed bracket after %${letter} — expected [Source,StartPosition,Length,SuppressZeros], e.g. %${letter}[0,0,0,0]`);
        i = close >= 0 ? close + 1 : s.length;
      }
    }
    const tok = { kind: 'term', letter, source, start, length, suppress, raw: s.slice(rawStart, i) };
    if (malformed) MALFORMED.add(tok);
    tokens.push(tok);
    sawTerm = true;

    const spec = TOKENS[letter];
    if (!spec) {
      errors.push(LETTER_HINTS[letter] ?? `Unknown date term %${letter}`);
      continue;
    }
    if (malformed) continue;
    if (source > 2) errors.push(`%${letter}: Source must be 0 (number), 1 (custom code) or 2 (three-letter name), not ${source}`);
    else if (source === 2 && !spec.abbr) errors.push(`%${letter}: three-letter names (Source 2) exist only for %M (month) and %d (day of week)`);
    else if (source === 1) warnings.push(`%${letter}: uses a custom code table defined on the printer — preview shows a placeholder`);
    if (suppress > 1) errors.push(`%${letter}: SuppressZeros must be 0 or 1, not ${suppress}`);
    if (spec.placeholder && source !== 1) warnings.push(`%${letter} (${spec.name.toLowerCase()}) is set by the printer — preview shows a placeholder`);
    const width = source === 2 ? 3 : spec.width;
    if (source <= 2 && start >= width && !spec.placeholder && source !== 1) {
      warnings.push(`%${letter}: StartPosition ${start} is past the end of the ${width}-character value — prints nothing`);
    }
  }
  flush();
  return { tokens, errors, warnings };
}

/** Strict parse. Throws DateFormatError on any error. */
export function parseFormat(str) {
  const { tokens, errors } = parseFormatDetailed(str);
  if (errors.length) throw new DateFormatError(errors[0], errors);
  return tokens;
}

/** @returns {{ ok: boolean, errors: string[], warnings: string[] }} */
export function validateFormat(str) {
  const { errors, warnings } = parseFormatDetailed(str);
  return { ok: errors.length === 0, errors, warnings };
}

/** All terms known, Source 0 or 2 only, no parse errors. */
export function isSupportedFormat(str) {
  const { tokens, errors } = parseFormatDetailed(str);
  if (errors.length) return false;
  return tokens.every((t) => t.kind !== 'term' || t.source === 0 || t.source === 2);
}

// ---------------------------------------------------------------------------
// Offsets
// ---------------------------------------------------------------------------

export class OffsetError extends RangeError {
  constructor(message, errors = [message]) {
    super(message);
    this.name = 'OffsetError';
    this.errors = errors;
  }
}

const INT32_MIN = -2147483648;
const INT32_MAX = 2147483647;
const INT_STRING = /^\s*[-+]?\d+\s*$/;
const OFFSET_KEYS = [['days', 'Offset days'], ['months', 'Offset months'], ['years', 'Offset years'], ['hours', 'Offset hours']];

/** One offset component → integer, or an error message. Missing/null/'' = 0. No truncation, no hex/exponent. */
function offsetPart(v, what) {
  if (v === undefined || v === null || v === '') return { value: 0 };
  let n = NaN;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string' && INT_STRING.test(v)) n = Number(v);
  if (!Number.isInteger(n)) return { error: `${what} must be a whole number` };
  if (n < INT32_MIN || n > INT32_MAX) return { error: `${what} must be between ${INT32_MIN} and ${INT32_MAX}` };
  return { value: n === 0 ? 0 : n }; // -0 → 0
}

/**
 * Non-throwing offset check.
 * @returns {{ ok: boolean, errors: string[], offset: {days, months, years, hours} | null }}
 */
export function validateOffset(offset) {
  if (offset != null && typeof offset !== 'object') return { ok: false, errors: ['Offset must be an object'], offset: null };
  const o = offset || {};
  const out = {};
  const errors = [];
  for (const [key, what] of OFFSET_KEYS) {
    const r = offsetPart(o[key], what);
    if (r.error) errors.push(r.error);
    else out[key] = r.value;
  }
  return errors.length ? { ok: false, errors, offset: null } : { ok: true, errors: [], offset: out };
}

/**
 * Offset-like object → `{ days, months, years, hours }` integers. Accepts integers and decimal integer
 * strings; throws OffsetError for anything else (6.9, '0x10', '1e3', NaN, booleans, out of Int32 range).
 */
export function normalizeOffset(offset) {
  const v = validateOffset(offset);
  if (!v.ok) throw new OffsetError(v.errors[0], v.errors);
  return v.offset;
}

function addMonthsClamped(d, n) {
  if (!n) return;
  const total = d.getMonth() + n;
  const year = d.getFullYear() + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  const lastDay = new Date(year, month + 1, 0).getDate();
  d.setFullYear(year, month, Math.min(d.getDate(), lastDay));
}

/**
 * Print date + offset, in local time. Order (our assumption, undocumented by CoLOS):
 * years (clamped to month end), then months (clamped), then days, then hours.
 */
export function applyOffset(date, offset) {
  const o = normalizeOffset(offset);
  const d = new Date(date.getTime());
  addMonthsClamped(d, o.years * 12);
  addMonthsClamped(d, o.months);
  if (o.days) d.setDate(d.getDate() + o.days);
  if (o.hours) d.setHours(d.getHours() + o.hours);
  return d;
}

// ---------------------------------------------------------------------------
// Presets & description
// ---------------------------------------------------------------------------

const off = (years, months) => Object.freeze({ days: 0, months, years, hours: 0 });

export const CUSTOM_PRESET = 'custom';

export const PRESETS = Object.freeze([
  { id: 'today', label: 'Today', offset: off(0, 0) },
  { id: '3m', label: 'Today + 3 months', offset: off(0, 3) },
  { id: '6m', label: 'Today + 6 months', offset: off(0, 6) },
  { id: '9m', label: 'Today + 9 months', offset: off(0, 9) },
  { id: '1y', label: 'Today + 1 year', offset: off(1, 0) },
  { id: '18m', label: 'Today + 18 months', offset: off(0, 18) },
  { id: '2y', label: 'Today + 2 years', offset: off(2, 0) },
  { id: '3y', label: 'Today + 3 years', offset: off(3, 0) },
].map(Object.freeze));

/** Exact integer field match against PRESETS (hours must be 0), else 'custom' (also for an invalid offset). */
export function presetFor(offset) {
  const v = validateOffset(offset);
  if (!v.ok) return CUSTOM_PRESET;
  const o = v.offset;
  const hit = PRESETS.find((p) => p.offset.days === o.days && p.offset.months === o.months
    && p.offset.years === o.years && p.offset.hours === o.hours);
  return hit ? hit.id : CUSTOM_PRESET;
}

const MINUS = '−';

/**
 * "Print date" | "Print date + 1 year 6 months" | "Print date − 3 days" | "Print date + 1 year − 3 days".
 * Same normalization as applyOffset: throws OffsetError for a non-integer offset.
 */
export function describeOffset(offset) {
  const o = normalizeOffset(offset);
  const parts = [[o.years, 'year'], [o.months, 'month'], [o.days, 'day'], [o.hours, 'hour']].filter(([n]) => n !== 0);
  let out = 'Print date';
  let prevSign = 0;
  for (const [n, unit] of parts) {
    const sign = Math.sign(n);
    const abs = Math.abs(n);
    const text = `${abs} ${unit}${abs === 1 ? '' : 's'}`;
    out += sign !== prevSign ? ` ${sign > 0 ? '+' : MINUS} ${text}` : ` ${text}`;
    prevSign = sign;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const pad = (n, w) => String(n).padStart(w, '0');

function isLeap(y) { return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0; }

/** Ordinal day of year, Jan 1 = 1 (29 Feb = 60 in leap years). */
function ordinalDay(d) {
  return Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(d.getFullYear(), 0, 1)) / 86400000) + 1;
}

/** Julian day where 29 Feb = 366 and every other date keeps its non-leap number. */
function julianFeb29Last(d) {
  const n = ordinalDay(d);
  if (!isLeap(d.getFullYear())) return n;
  if (d.getMonth() === 1 && d.getDate() === 29) return 366;
  return n > 60 ? n - 1 : n;
}

/** ISO-8601 week number. */
export function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dow = (t.getUTCDay() + 6) % 7; // Mon = 0
  t.setUTCDate(t.getUTCDate() - dow + 3); // Thursday of this week
  const yearStart = Date.UTC(t.getUTCFullYear(), 0, 1);
  return 1 + Math.floor((t - yearStart) / 86400000 / 7);
}

function numericValue(letter, d) {
  switch (letter) {
    case 'Y': return pad(d.getFullYear(), 4);
    case 'M': return pad(d.getMonth() + 1, 2);
    case 'D': return pad(d.getDate(), 2);
    case 'd': return String(((d.getDay() + 6) % 7) + 1); // 1 = Monday … 7 = Sunday (assumption)
    case 'J': return pad(julianFeb29Last(d), 3);
    case 'j': return pad(ordinalDay(d), 3);
    case 'H': return pad(d.getHours() % 12 || 12, 2);
    case 'h': return pad(d.getHours(), 2);
    case 'm': return pad(d.getMinutes(), 2);
    case 's': return pad(d.getSeconds(), 2);
    case 'w': return pad(isoWeek(d), 2);
    case 'a': return d.getHours() < 12 ? 'AM' : 'PM';
    default: return null;
  }
}

function slice(value, start, length) {
  return length > 0 ? value.substr(start, length) : value.slice(start);
}

/**
 * Render a format string for a date.
 * @returns {{ text: string, approximate: boolean }} approximate = the printer may print something different
 *   (placeholder terms, custom codes, unverified numbering, non-English names, or an invalid format).
 */
export function formatDateDetailed(str, date, language = 'ENGLISH') {
  const { tokens, errors } = parseFormatDetailed(str);
  let approximate = errors.length > 0;
  let text = '';
  for (const t of tokens) {
    if (t.kind === 'literal') { text += t.text; continue; }
    const spec = TOKENS[t.letter];
    if (!spec || MALFORMED.has(t) || t.source > 2) { text += t.raw; approximate = true; continue; }
    if (t.source === 1) { text += '[code]'; approximate = true; continue; }
    if (spec.placeholder) { text += spec.placeholder; approximate = true; continue; }
    let value;
    let numeric = false;
    if (t.source === 2) {
      if (!spec.abbr) { text += t.raw; approximate = true; continue; }
      value = t.letter === 'M' ? MONTH_ABBR[date.getMonth()] : DAY_ABBR[(date.getDay() + 6) % 7];
      if (language !== 'ENGLISH') approximate = true;
    } else {
      value = numericValue(t.letter, date);
      numeric = t.letter !== 'a';
      if (t.letter === 'd') approximate = true; // day-of-week numbering unverified
    }
    value = slice(value, t.start, t.length);
    if (numeric && t.suppress === 1 && value.length > 1) value = value.replace(/^0+(?=.)/, '');
    text += value;
  }
  return { text, approximate };
}

/** Preview string (see formatDateDetailed for the approximate flag). */
export function formatDate(str, date, language = 'ENGLISH') {
  return formatDateDetailed(str, date, language).text;
}

// ---------------------------------------------------------------------------
// Named formats & user-facing choices
// ---------------------------------------------------------------------------

/** Named formats shipped with CoLOS Designer 6.2 (name → format string). */
export const NAMED_FORMATS = Object.freeze({
  'dd.MM.yyyy': '%D.%M.%Y',
  'dd.MM.yy': '%D.%M.%Y[0,2,2,0]',
  'dd/MM/yy': '%D/%M/%Y[0,2,2,0]',
  'dd MM yyyy': '%D %M %Y',
  'dd MM yy': '%D %M %Y[0,2,2,0]',
  'dd MMM yyyy': '%D %M[2,0,0,0] %Y',
  'dd MMM yy': '%D %M[2,0,0,0] %Y[0,2,2,0]',
  'dd/MMM/yy': '%D/%M[2,0,0,0]/%Y[0,2,2,0]',
  'ddMMyy': '%D%M%Y[0,2,2,0]',
  'MM/yyyy': '%M/%Y',
  'MMM yyyy': '%M[2,0,0,0] %Y',
  'MMM yy': '%M[2,0,0,0] %Y[0,2,2,0]',
  'MMM/yy': '%M[2,0,0,0]/%Y[0,2,2,0]',
  'MM/dd/yy': '%M/%D/%Y[0,2,2,0]',
  'yyyyMMdd': '%Y%M%D',
  'yyMMdd': '%Y[0,2,2,0]%M%D',
  'yy/MM/dd': '%Y[0,2,2,0]/%M/%D',
  'yyyy': '%Y',
  'MMM d yyyy': '%M[2,0,3,0] %D[0,0,2,1] %Y[0,0,4,0]',
  'DDD': '%J[0,0,3,0]',
  'ddd': '%j[0,0,3,0]',
  'ww': '%w',
  'hh:mm': '%h:%m',
});

/** Fixed date used for the example in FORMAT_CHOICES labels. */
export const EXAMPLE_DATE = new Date(2028, 8, 15);

/** Formats seen on real printed labels (corpus). */
const VERIFIED = new Set(['%D.%M.%Y', '%D/%M/%Y[0,2,2,0]']);

export const FORMAT_CHOICES = Object.freeze([
  ['dd.mm.yyyy', 'DD.MM.YYYY', '%D.%M.%Y'],
  ['dd/mm/yy', 'DD/MM/YY', '%D/%M/%Y[0,2,2,0]'],
  ['dd.mm.yy', 'DD.MM.YY', '%D.%M.%Y[0,2,2,0]'],
  ['dd/mm/yyyy', 'DD/MM/YYYY', '%D/%M/%Y'],
  ['dd-mmm-yyyy', 'DD MMM YYYY', '%D %M[2,0,0,0] %Y'],
  ['mmm-yyyy', 'MMM YYYY', '%M[2,0,0,0] %Y'],
  ['mm/yyyy', 'MM/YYYY', '%M/%Y'],
  ['yyyymmdd', 'YYYYMMDD', '%Y%M%D'],
].map(([id, pattern, value]) => {
  const example = formatDate(value, EXAMPLE_DATE);
  const verified = VERIFIED.has(value);
  return Object.freeze({
    id,
    label: `${pattern} (${example})`,
    pattern,
    example,
    value,
    verified,
    note: verified ? 'Used on labels already printed' : 'Shipped with CoLOS; not yet test-printed',
  });
}));

/** The FORMAT_CHOICES entry for a format string, or null. */
export function choiceFor(value) {
  return FORMAT_CHOICES.find((c) => c.value === value) ?? null;
}

// ---------------------------------------------------------------------------
// UI convenience
// ---------------------------------------------------------------------------

/**
 * @param field date Field ({ format, language, offset, prompting })
 * @returns {{ printDate: string, value: string, description: string, approximate: boolean, prompted: boolean }}
 *   printDate = `now` in the field's format; value = what prints (now + offset).
 */
export function previewLines(field, now = new Date()) {
  const format = field?.format ?? '';
  const language = field?.language ?? 'ENGLISH';
  const printed = formatDateDetailed(format, now, language);
  const result = formatDateDetailed(format, applyOffset(now, field?.offset), language);
  return {
    printDate: printed.text,
    value: result.text,
    description: describeOffset(field?.offset),
    approximate: result.approximate,
    prompted: field?.prompting === 'PROMPT_YY_MM_DD',
  };
}

// ---------------------------------------------------------------------------
// Fixed dates and offset sanity
// ---------------------------------------------------------------------------

/**
 * A date field is always "printer clock + offset" — the file has nowhere to store a calendar date.
 * To print ONE exact date (e.g. a production date for a batch) the field's data source becomes plain
 * Static text, the same kind of text the real labels already use for product names.
 */
const TIME_LETTERS = new Set(['h', 'H', 'm', 's', 'a']);

/**
 * The text a fixed date prints as.
 * @param {string} format  CoLOS format string, e.g. '%D.%M.%Y'
 * @param {Date} date      the calendar date to print (local time)
 * @returns {{ ok: boolean, text: string, reason?: string }}
 */
export function fixedDateText(format, date, language = 'ENGLISH') {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return { ok: false, text: '', reason: 'Pick a valid date.' };
  const { tokens, errors } = parseFormatDetailed(format ?? '');
  if (errors.length) return { ok: false, text: '', reason: errors[0] };
  if (tokens.some((t) => t.kind === 'term' && TIME_LETTERS.has(t.letter))) {
    return { ok: false, text: '', reason: 'This format includes a time of day. A fixed date needs a date-only format.' };
  }
  const r = formatDateDetailed(format, date, language);
  if (r.approximate) {
    return { ok: false, text: r.text, reason: "The editor can't show this format exactly, so it can't turn it into fixed text. Choose a numeric format such as DD.MM.YYYY." };
  }
  if (!r.text.trim()) return { ok: false, text: '', reason: 'This format prints nothing.' };
  return { ok: true, text: r.text };
}

/** Offsets beyond these (in their own unit) are almost certainly a typed date, not an offset. */
export const OFFSET_LIMITS = Object.freeze({ years: 30, months: 360, days: 3650, hours: 87600 });

/**
 * A plain-English warning when an offset is absurdly far from the print date (typically someone typing
 * 2026 / 8 / 26 into Years / Months / Days), else null. Invalid offsets return null (validateOffset reports them).
 */
export function suspiciousOffset(offset) {
  const v = validateOffset(offset);
  if (!v.ok) return null;
  for (const [key, unit] of [['years', 'year'], ['months', 'month'], ['days', 'day'], ['hours', 'hour']]) {
    const n = Math.abs(v.offset[key]);
    if (n > OFFSET_LIMITS[key]) {
      return `${n} ${unit}s is a very long way from the print date. Offsets count from the day the label is printed — to print one exact date, choose Fixed date.`;
    }
  }
  return null;
}

/**
 * If the offset looks like a calendar date typed into the Years / Months / Days boxes (e.g. 2026 / 8 / 26),
 * return it as `{ year, month, day }` (month 1–12) so the UI can offer to use it as a fixed date; else null.
 */
export function typedDateInOffset(offset) {
  const v = validateOffset(offset);
  if (!v.ok) return null;
  const { years: year, months: month, days: day, hours } = v.offset;
  if (hours !== 0 || year < 2000 || year > 2099 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day, 12);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day ? { year, month, day } : null;
}
